import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Search,
  Building2,
  Phone,
  Mail,
  MapPin,
  X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SalesLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLead, setNewLead] = useState({
    school_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    city: "",
    state: "",
    estimated_students: 100,
    source: "website",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const res = await fetch(`${API}/api/sales/leads`, { credentials: "include" });
      const data = await res.json();
      if (data.success) {
        setLeads(data.leads);
      }
    } catch (error) {
      console.error("Failed to fetch leads:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLead = async () => {
    if (!newLead.school_name || !newLead.contact_name || !newLead.contact_email) {
      toast.error("Please fill in required fields");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/sales/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newLead),
      });

      const data = await res.json();
      if (data.success) {
        toast.success("Lead added successfully!");
        setLeads([data.lead, ...leads]);
        setShowAddModal(false);
        setNewLead({
          school_name: "",
          contact_name: "",
          contact_email: "",
          contact_phone: "",
          city: "",
          state: "",
          estimated_students: 100,
          source: "website",
          notes: "",
        });
      } else {
        toast.error(data.detail || "Failed to add lead");
      }
    } catch (error) {
      console.error("Failed to add lead:", error);
      toast.error("Failed to add lead");
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: "bg-blue-100 text-blue-700",
      contacted: "bg-yellow-100 text-yellow-700",
      demo_scheduled: "bg-purple-100 text-purple-700",
      proposal_sent: "bg-orange-100 text-orange-700",
      negotiating: "bg-pink-100 text-pink-700",
      won: "bg-green-100 text-green-700",
      lost: "bg-red-100 text-red-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  const filteredLeads = leads.filter(
    (lead) =>
      lead.school_name?.toLowerCase().includes(search.toLowerCase()) ||
      lead.contact_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
          <p className="text-gray-500">{leads.length} total leads</p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-gradient-to-r from-blue-600 to-purple-600"
          data-testid="add-lead-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Lead
        </Button>
      </div>

      {/* Search */}
      <Card className="bg-white shadow-sm border-gray-200">
        <CardContent className="py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search leads..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-gray-50"
              data-testid="search-leads-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Leads List */}
      {filteredLeads.length === 0 ? (
        <Card className="bg-white shadow-sm border-gray-200">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No leads yet</h3>
            <p className="text-gray-500 mb-4">Add your first lead to get started</p>
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredLeads.map((lead) => (
            <Card
              key={lead.id}
              className="bg-white shadow-sm border-gray-200 hover:shadow-md transition-shadow"
            >
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge className={getStatusColor(lead.status)}>
                        {lead.status?.replace("_", " ")}
                      </Badge>
                      <span className="text-sm text-gray-500 font-mono">{lead.id}</span>
                    </div>
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      {lead.school_name}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">{lead.contact_name}</p>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                      {lead.contact_email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {lead.contact_email}
                        </span>
                      )}
                      {lead.contact_phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {lead.contact_phone}
                        </span>
                      )}
                      {(lead.city || lead.state) && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {[lead.city, lead.state].filter(Boolean).join(", ")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Est. Students</p>
                    <p className="font-semibold text-gray-900">{lead.estimated_students}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Lead Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg bg-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Add New Lead</CardTitle>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>School Name *</Label>
                  <Input
                    value={newLead.school_name}
                    onChange={(e) =>
                      setNewLead({ ...newLead, school_name: e.target.value })
                    }
                    placeholder="e.g., Delhi Public School"
                    data-testid="new-lead-school-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Name *</Label>
                  <Input
                    value={newLead.contact_name}
                    onChange={(e) =>
                      setNewLead({ ...newLead, contact_name: e.target.value })
                    }
                    placeholder="Principal name"
                    data-testid="new-lead-contact-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Email *</Label>
                  <Input
                    type="email"
                    value={newLead.contact_email}
                    onChange={(e) =>
                      setNewLead({ ...newLead, contact_email: e.target.value })
                    }
                    placeholder="email@school.edu.in"
                    data-testid="new-lead-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={newLead.contact_phone}
                    onChange={(e) =>
                      setNewLead({ ...newLead, contact_phone: e.target.value })
                    }
                    placeholder="+91 98765 43210"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Estimated Students</Label>
                  <Input
                    type="number"
                    value={newLead.estimated_students}
                    onChange={(e) =>
                      setNewLead({
                        ...newLead,
                        estimated_students: parseInt(e.target.value) || 0,
                      })
                    }
                    min={25}
                  />
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={newLead.city}
                    onChange={(e) =>
                      setNewLead({ ...newLead, city: e.target.value })
                    }
                    placeholder="Mumbai"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={newLead.state}
                    onChange={(e) =>
                      setNewLead({ ...newLead, state: e.target.value })
                    }
                    placeholder="Maharashtra"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Notes</Label>
                  <Input
                    value={newLead.notes}
                    onChange={(e) =>
                      setNewLead({ ...newLead, notes: e.target.value })
                    }
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600"
                  onClick={handleAddLead}
                  disabled={saving}
                  data-testid="save-lead-btn"
                >
                  {saving ? "Saving..." : "Add Lead"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
